import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { BriefForm } from '@/components/wizard/brief/brief-form';
import {
  BriefIntro,
  BriefLoading,
} from '@/components/wizard/brief/brief-intro';
import {
  briefResumeFrom,
  type BriefResumeContext,
} from '@/components/wizard/brief/brief-form/use-brief-flow';
import { useSuggestionOrigin } from '@/components/wizard/brief/brief-form/use-suggestion-origin';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { useWizardSession } from '@/hooks/data/use-wizard';
import { loadNamespaces } from '@/lib/i18n/i18n';

const searchSchema = z.object({
  strategyId: z.coerce.number().int().positive().optional(),
  recommendationId: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute('/_authenticated/wizard/brief')({
  validateSearch: searchSchema,
  staticData: {
    title: 'route.wizard_brief.title',
    messageContext: { context_type: 'wizard', idParam: 'strategyId' },
  },
  // The single-campaign goal picker says so when no goal is set up.
  loader: () => loadNamespaces('goals'),
  component: BriefRoute,
});

function BriefRoute(): ReactElement {
  const { strategyId, recommendationId } = Route.useSearch();
  const { data: user } = useQuery({ ...meQueryOptions, retry: false });
  const session = useWizardSession(strategyId ?? null);

  const resuming = strategyId != null;
  // A resumed draft already carries its suggestion, so only a fresh brief reads one.
  const origin = useSuggestionOrigin(resuming ? undefined : recommendationId);
  const resume: BriefResumeContext | undefined =
    resuming && session.data
      ? briefResumeFrom(strategyId, session.data)
      : undefined;

  return (
    <div className="flex flex-col">
      <BriefIntro />
      {resuming && session.isLoading ? (
        <BriefLoading />
      ) : (
        <BriefForm
          key={strategyId ?? 'new'}
          user={user}
          resume={resume}
          origin={origin}
          fromSuggestion={recommendationId != null}
        />
      )}
    </div>
  );
}
